import * as THREE from 'three';
import { useMemo } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import { TextureLoader } from 'three/src/loaders/TextureLoader.js';
import { useGameStore } from '../stores/gameStore.js';

import particleVertexShader from '../shaders/particles/vertex.glsl';
import particleFragmentShader from '../shaders/particles/fragment.glsl';

export default function CustomParticles({ startX = 400, endX = 900, count = 2000 }) {
    const phase = useGameStore(state => state.phase);

    const textures = useLoader(TextureLoader, [
        '/particles/1.png',
        '/particles/2.png',
        '/particles/3.png',
    ]);

    const uniforms = useMemo(() => ({
        uTime: { value: 0 },
        uLocalTime: { value: 0 },
        uTravelDistance: { value: 0 },
        uBoundsStart: { value: startX },
        uBoundsEnd: { value: endX },
        uTexture1: { value: textures[0] },
        uTexture2: { value: textures[1] },
        uTexture3: { value: textures[2] },
    }), [textures]);

    const geometry = useMemo(() => {
        const geo = new THREE.BufferGeometry();

        const positions = new Float32Array(count * 3);
        const sizes = new Float32Array(count);
        const animationSpeeds = new Float32Array(count);
        const animationOffsets = new Float32Array(count);
        const textureIndices = new Float32Array(count);
        const moveSpeeds = new Float32Array(count);

        for (let i = 0; i < count; i++) {
            // Posicions X repartides dintre del rang establert
            positions[i * 3 + 0] = startX + Math.random() * (endX - startX);

            // Random Y i Z (amplitud perquè omplin la pantalla per davant del jugador)
            positions[i * 3 + 1] = (Math.random() - 0.5) * 50;
            positions[i * 3 + 2] = (Math.random() - 0.5) * 50;

            sizes[i] = 20 + Math.random() * 30;
            animationSpeeds[i] = Math.random() * 5;
            animationOffsets[i] = 2.0 + Math.random() * 3.0;
            textureIndices[i] = Math.floor(Math.random() * 3);

            // La clau "moviment constant X positiu": 
            // Cada partícula corre cap a +X a una velocitat moderada
            // perquè es quedin on toquen i la nau interactuï amb elles.
            moveSpeeds[i] = 1.0 + Math.random() * 8.0;
        }

        geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
        geo.setAttribute('aAnimationSpeed', new THREE.BufferAttribute(animationSpeeds, 1));
        geo.setAttribute('aAnimationOffset', new THREE.BufferAttribute(animationOffsets, 1));
        geo.setAttribute('aTextureIndex', new THREE.BufferAttribute(textureIndices, 1));
        geo.setAttribute('aMoveSpeed', new THREE.BufferAttribute(moveSpeeds, 1));

        return geo;
    }, [startX, endX, count]);

    useFrame((state, delta) => {
        if (phase !== 'playing' && phase !== 'launching') return;

        uniforms.uTime.value = state.clock.getElapsedTime();
        uniforms.uLocalTime.value += delta;

        const camX = state.camera.position.x;
        let globalSpeedMultiplier = 1.0;

        // Exactament la mateixa corba d'acceleració matemàtica que les SpeedLines per anar sincronitzat!
        if (phase === 'playing' || phase === 'launching') {
            globalSpeedMultiplier = 0.5; // Multiplicador suau passiu

            if (camX >= 380) {
                const progress = THREE.MathUtils.clamp((camX - 380) / (563 - 380), 0, 1);

                // Ajustem l'acceleració per tenir un punt entremig perfecte
                const smoothExp = Math.pow(progress, 4);
                const hyperExp = Math.pow(progress, 12);

                globalSpeedMultiplier = 0.5 + (smoothExp * 30.0) + (hyperExp * 170.0);
            }
        }

        // Enlloc de multiplicar en absolut al shader (que causaria teletransports visuals),
        // anem sumant la distància fraccionada per frame permetent una acceleració controlada!
        uniforms.uTravelDistance.value += delta * globalSpeedMultiplier;
    });

    return (
        <points geometry={geometry} visible={phase === 'playing' || phase === 'launching'}>
            <shaderMaterial
                vertexShader={particleVertexShader}
                fragmentShader={particleFragmentShader}
                uniforms={uniforms}
                transparent={true}
                depthWrite={false}
                blending={THREE.AdditiveBlending}
            />
        </points>
    );
}